import React, { useState } from 'react';
import { Formik, Form } from "formik";
import * as Yup from "yup";
import bcrypt from 'bcryptjs';
import { useNavigate } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import Login_bg from '../assets/images/login_screen_img.jpg'
import Button from '../components/Button';
import { successMessage, errorMessage } from '../components/Toaster';
import DialogModal from '../components/Dialog';
import { SUPERADMIN } from "../constants";
import { login, changePassword } from '../utils/api';
import { getErrorMessage } from "../utils/error";

const Login = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [isPasswordSet, setIsPasswordSet] = useState(true);
    const [openModal, setOpenModal] = useState(false);

    const validationSchema = Yup.object({
        userName: Yup.string().required('User name is required'),
        password: Yup.string().required('Password is required'),
        oldPassword: Yup.string().when('isPasswordSet', {
            is: false,
            then: (schema) => schema.required('Old password is required'),
            otherwise: (schema) => schema.notRequired(),
        }),
        newPassword: Yup.string().when('isPasswordSet', {
            is: false,
            then: (schema) => schema.required('New password is required').min(8, 'Password must be at least 8 characters'),
            otherwise: (schema) => schema.notRequired(),
        }),
        confirmPassword: Yup.string().when('isPasswordSet', {
            is: false,
            then: (schema) =>
                schema.oneOf([Yup.ref('newPassword')], 'Passwords must match').required('Confirm password is required'),
            otherwise: (schema) => schema.notRequired(),
        }),
    });

    const handleLogin = async (values) => {
        try {
            setLoading(true);
            const response = await login({ user_name: values.userName, password: values.password });
            const {
                is_password_set,
                user_id,
                user_name,
                first_name,
                last_name,
                email,
                phone_number,
                company_id,
                company_name,
                role_name,
                jwt_token,
                location_name
            } = response.data;

            setLoading(false);

            if (role_name !== SUPERADMIN) {
                errorMessage("Access denied: You do not have the required role.");
                return;
            }

            localStorage.setItem("user_id", user_id);
            localStorage.setItem("user_name", user_name);
            localStorage.setItem("company_id", company_id);
            localStorage.setItem("company_name", company_name);
            localStorage.setItem("auth_token", jwt_token);
            localStorage.setItem("first_name", first_name);
            localStorage.setItem("last_name", last_name);
            localStorage.setItem("email", email || "");
            localStorage.setItem("phone_number", phone_number || "");
            localStorage.setItem("role_name", role_name);
            localStorage.setItem("location_name", location_name ?? "");

            if (is_password_set) {
                const redirectTo = localStorage.getItem("redirectAfterLogin");

                if (redirectTo) {
                    localStorage.removeItem("redirectAfterLogin");
                    const updatedRedirect = redirectTo.replace("protected=true", "protected=false");
                    navigate(updatedRedirect);
                } else {
                    navigate("/scans");
                }
            } else {
                setIsPasswordSet(false);
            }
        } catch (error) {
            setLoading(false);
            const message = getErrorMessage(error);
            errorMessage(message || 'Login failed.');
        }
    };

    const handleChangePassword = async (values) => {
        if (values.oldPassword === values.newPassword) {
            errorMessage('New password cannot be the same as the old password.');
            return;
        }

        if (values.newPassword !== values.confirmPassword) {
            errorMessage('New password and confirm password must be the same.');
            return;
        }

        try {
            setLoading(true);
            const hashedPassword = await bcrypt.hash(values.newPassword, 10);
            const response = await changePassword({
                user_name: values.userName,
                old_password: values.oldPassword,
                new_password: hashedPassword
            });
            setLoading(false);
            successMessage(response.message);
            setIsPasswordSet(true);
        } catch (error) {
            setLoading(false);
            const message = getErrorMessage(error);
            errorMessage(message || 'Password change failed.');
        }
    };

    const handleOpenModal = () => setOpenModal(true);
    const handleCloseModal = () => setOpenModal(false);

    return (
        <div className="APP-background">
            <div className="d-flex justify-start flex-direction-row">
                <div className="w-50 bg-blue h-100vh d-flex justify-between flex-direction-column">
                    <div className="d-flex justify-center">
                        <img src={Login_bg} alt="logo" className="w-100 h-100vh" />
                    </div>
                </div>
                <Formik
                    initialValues={{
                        userName: '',
                        password: '',
                        oldPassword: '',
                        newPassword: '',
                        confirmPassword: '',
                    }}
                    validationSchema={validationSchema}
                    onSubmit={(values) => {
                        if (isPasswordSet) {
                            handleLogin(values);
                        } else {
                            handleChangePassword(values);
                        }
                    }}
                    enableReinitialize
                >
                    {({
                        values,
                        errors,
                        touched,
                        setFieldValue,
                        handleSubmit,
                        resetForm
                    }) => (
                        <Form onSubmit={handleSubmit} className="w-65 h-100vh">
                            <div className='h-100vh d-flex justify-center align-center flex-direction-column'>
                                <div className="d-flex flex-direction-column justify-center align-center">
                                    <h3 className="APP-color">{isPasswordSet ? 'Login' : 'Change Password'}</h3>
                                    <p>
                                        {isPasswordSet
                                            ? 'Enter your credentials to log in'
                                            : 'Enter details to change your password'}
                                    </p>
                                </div>
                                <TextField
                                    className="w-400px m-t-20"
                                    label="User Name"
                                    onChange={(e) => setFieldValue('userName', e.target.value)}
                                    variant="standard"
                                    value={values.userName}
                                    error={touched.userName && Boolean(errors.userName)}
                                    helperText={touched.userName && errors.userName}
                                />
                                {isPasswordSet ? (
                                    <TextField
                                        className="w-400px m-t-10"
                                        label="Password"
                                        onChange={(e) => setFieldValue('password', e.target.value)}
                                        variant="standard"
                                        type="password"
                                        value={values.password}
                                        error={touched.password && Boolean(errors.password)}
                                        helperText={touched.password && errors.password}
                                    />
                                ) : (
                                    <>
                                        <TextField
                                            required
                                            className="w-400px m-t-10"
                                            label="Old Password"
                                            onChange={(e) => setFieldValue('oldPassword', e.target.value)}
                                            variant="standard"
                                            type="password"
                                            value={values.oldPassword}
                                            error={touched.oldPassword && Boolean(errors.oldPassword)}
                                            helperText={touched.oldPassword && errors.oldPassword}
                                        />
                                        <TextField
                                            required
                                            className="w-400px m-t-10"
                                            label="New Password"
                                            onChange={(e) => setFieldValue('newPassword', e.target.value)}
                                            variant="standard"
                                            type="password"
                                            value={values.newPassword}
                                            error={touched.newPassword && Boolean(errors.newPassword)}
                                            helperText={touched.newPassword && errors.newPassword}
                                        />
                                        <TextField
                                            required
                                            className="w-400px m-t-10"
                                            label="Confirm Password"
                                            onChange={(e) => setFieldValue('confirmPassword', e.target.value)}
                                            variant="standard"
                                            type="password"
                                            value={values.confirmPassword}
                                            error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                                            helperText={touched.confirmPassword && errors.confirmPassword}
                                        />
                                    </>
                                )}
                                <Button
                                    className="white APP-border w-400px m-t-10"
                                    color="success"
                                    variant="contained"
                                    type="submit"
                                    loading={loading}
                                    disabled={loading}
                                    label={loading ? 'Processing...' : isPasswordSet ? 'Login' : 'Change Password'}
                                />
                                <p className="text-center w-100">
                                    {isPasswordSet ? 'Forgot your password?' : 'Back to login?'}{' '}
                                    <Button
                                        className="APP-color APP-border"
                                        onClick={() => {
                                            if (isPasswordSet) {
                                                handleOpenModal();
                                            } else {
                                                setIsPasswordSet(true);
                                                resetForm();
                                            }
                                        }}
                                        color="primary"
                                        label={isPasswordSet ? 'Click Here' : 'Login'}
                                    />
                                </p>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
            <DialogModal
                open={openModal}
                onClose={handleCloseModal}
                onConfirm={handleCloseModal}
                title="Contact your Administrator"
                message="Please reach out to your administrator for assistance."
                confirmButtonText="OK"
                cancelButtonText="Cancel"
            />
        </div>
    );
};

export default Login;
